import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings, Users, Building2, UserCog, Upload, Wrench, Package, CheckCircle,
  HelpCircle, Construction, UploadCloud, FileText, Zap, Code, BarChart3,
  Shield, CheckCircle2, Lock, Link2, Terminal, GitBranch, Sun, Moon, Monitor, Phone,
  CalendarCheck, ImageIcon, Smartphone, GraduationCap
} from 'lucide-react';

const MoreOptions = () => {
  const navigate = useNavigate();
  const [themeMode, setThemeMode] = useState('system');
  const [activeOption, setActiveOption] = useState('');

  const moreOptions = [
    // System Management
    {
      id: 'settings',
      title: 'Settings',
      subtitle: 'System configuration',
      icon: Settings,
      color: '#6b7280',
      backgroundColor: '#f9fafb',
      category: 'System Management'
    },
    {
      id: 'team-leaders',
      title: 'Team Leaders',
      subtitle: 'Manage team leaders',
      icon: Users,
      color: '#0891b2',
      backgroundColor: '#f0f9ff',
      category: 'System Management'
    },
    {
      id: 'garage-management',
      title: 'Garage Management',
      subtitle: 'Manage partner garages',
      icon: Building2,
      color: '#16a34a',
      backgroundColor: '#f0fdf4',
      category: 'System Management'
    },
    {
      id: 'user-management',
      title: 'User Management',
      subtitle: 'Advanced user controls',
      icon: UserCog,
      color: '#7c3aed',
      backgroundColor: '#f5f3ff',
      category: 'System Management'
    },
    {
      id: 'employee-attendance',
      title: 'Employee Attendance',
      subtitle: 'View attendance of all employees',
      icon: CalendarCheck,
      color: '#0ea5e9',
      backgroundColor: '#f0f9ff',
      category: 'System Management'
    },
    {
      id: 'blog-admin',
      title: 'Marketing Site / Blog',
      subtitle: 'Blog, keywords & website leads (app.roadserve.in)',
      icon: FileText,
      color: '#1B4F9C',
      backgroundColor: '#eff6ff',
      category: 'System Management'
    },
    {
      id: 'customer-banners',
      title: 'Customer App Banners',
      subtitle: 'Home screen carousel for customer app',
      icon: ImageIcon,
      color: '#2563eb',
      backgroundColor: '#eff6ff',
      category: 'System Management'
    },
    {
      id: 'mechanic-banners',
      title: 'Mechanic App Banners',
      subtitle: 'Home screen carousel for mechanic / partner app',
      icon: ImageIcon,
      color: '#0F2744',
      backgroundColor: '#eef2ff',
      category: 'System Management'
    },
    {
      id: 'partner-training',
      title: 'Partner Training',
      subtitle: 'Add video URLs shown in partner Profile → Professional',
      icon: GraduationCap,
      color: '#1B4F9C',
      backgroundColor: '#eff6ff',
      category: 'System Management'
    },

    {
      id: 'leads-csv-upload',
      title: 'Leads CSV Upload',
      subtitle: 'Upload leads for telecallers',
      icon: Upload,
      color: '#059669',
      backgroundColor: '#ecfdf5',
      category: 'System Management'
    },
    {
      id: 'wfh-leads-csv-upload',
      title: 'WFH Leads Upload',
      subtitle: 'Upload + churn WFH batch leads',
      icon: UploadCloud,
      color: '#2563eb',
      backgroundColor: '#eff6ff',
      category: 'System Management'
    },
    {
      id: 'wfh-employee-report',
      title: 'WFH Employee Report',
      subtitle: 'Telecaller-wise WFH lead status + performance',
      icon: BarChart3,
      color: '#0ea5e9',
      backgroundColor: '#f0f9ff',
      category: 'System Management'
    },
    {
      id: 'mechanic-csv-upload',
      title: 'Mechanic CSV Upload',
      subtitle: 'Upload mechanics with service areas',
      icon: Wrench,
      color: '#dc2626',
      backgroundColor: '#fef2f2',
      category: 'System Management'
    },
    {
      id: 'mechanic-management',
      title: 'Mechanic Management',
      subtitle: 'Manage mechanic details and service areas',
      icon: Wrench,
      color: '#7c2d12',
      backgroundColor: '#fef7ed',
      category: 'System Management'
    },
    {
      id: 'app-version-control',
      title: 'App Version Control',
      subtitle: 'Force / prompt store update for Customer, Partner, Staff apps',
      icon: Smartphone,
      color: '#4f46e5',
      backgroundColor: '#eef2ff',
      category: 'System Management'
    },
    {
      id: 'mechanic-kyc',
      title: 'Mechanic KYC Verify',
      subtitle: 'Approve docs — only verified partners get a public page',
      icon: CheckCircle,
      color: '#059669',
      backgroundColor: '#ecfdf5',
      category: 'System Management'
    },
    {
      id: 'shipment-tracking',
      title: 'Shipment Tracking',
      subtitle: 'Track shipments & lead quality',
      icon: Package,
      color: '#dc2626',
      backgroundColor: '#fef2f2',
      category: 'System Management'
    },
    {
      id: 'delivered-leads',
      title: 'Delivered Leads',
      subtitle: 'Manage and track delivered leads',
      icon: CheckCircle,
      color: '#059669',
      backgroundColor: '#ecfdf5',
      category: 'System Management'
    },
    // Support & Maintenance
    {
      id: 'support-tickets',
      title: 'Support Tickets',
      subtitle: 'Customer support system',
      icon: HelpCircle,
      color: '#a21caf',
      backgroundColor: '#fef7ff',
      category: 'Support & Maintenance'
    },
    {
      id: 'system-maintenance',
      title: 'System Maintenance',
      subtitle: 'System health & updates',
      icon: Construction,
      color: '#f59e0b',
      backgroundColor: '#fffbeb',
      category: 'Support & Maintenance'
    },
    {
      id: 'backup-restore',
      title: 'Backup & Restore',
      subtitle: 'Data management',
      icon: UploadCloud,
      color: '#ef4444',
      backgroundColor: '#fef2f2',
      category: 'Support & Maintenance'
    },
    {
      id: 'system-logs',
      title: 'System Logs',
      subtitle: 'Error logs & monitoring',
      icon: FileText,
      color: '#374151',
      backgroundColor: '#f8fafc',
      category: 'Support & Maintenance'
    },
    // Advanced Features
    {
      id: 'workflow-automation',
      title: 'Workflow Automation',
      subtitle: 'Automated processes',
      icon: Zap,
      color: '#0284c7',
      backgroundColor: '#f0f9ff',
      category: 'Advanced Features'
    },
    {
      id: 'api-management',
      title: 'API Management',
      subtitle: 'External integrations',
      icon: Code,
      color: '#7c3aed',
      backgroundColor: '#f5f3ff',
      category: 'Advanced Features'
    },
    {
      id: 'data-analytics',
      title: 'Data Analytics',
      subtitle: 'Advanced analytics',
      icon: BarChart3,
      color: '#dc2626',
      backgroundColor: '#fef2f2',
      category: 'Advanced Features'
    },
    // Security & Compliance
    {
      id: 'security-settings',
      title: 'Security Settings',
      subtitle: 'Access control & security',
      icon: Shield,
      color: '#059669',
      backgroundColor: '#ecfdf5',
      category: 'Security & Compliance'
    },
    {
      id: 'compliance-reports',
      title: 'Compliance Reports',
      subtitle: 'Regulatory compliance',
      icon: CheckCircle2,
      color: '#16a34a',
      backgroundColor: '#f0fdf4',
      category: 'Security & Compliance'
    },
    {
      id: 'privacy-settings',
      title: 'Privacy Settings',
      subtitle: 'Data privacy controls',
      icon: Lock,
      color: '#7c3aed',
      backgroundColor: '#f5f3ff',
      category: 'Security & Compliance'
    },
    // Integration & Tools
    {
      id: 'third-party-integrations',
      title: 'Third-party Integrations',
      subtitle: 'External service connections',
      icon: Link2,
      color: '#0891b2',
      backgroundColor: '#f0f9ff',
      category: 'Integration & Tools'
    },
    {
      id: 'developer-tools',
      title: 'Developer Tools',
      subtitle: 'Development utilities',
      icon: Terminal,
      color: '#374151',
      backgroundColor: '#f8fafc',
      category: 'Integration & Tools'
    },
    {
      id: 'webhook-management',
      title: 'Webhook Management',
      subtitle: 'Event notifications',
      icon: GitBranch,
      color: '#f59e0b',
      backgroundColor: '#fffbeb',
      category: 'Integration & Tools'
    },
    {
      id: 'call-provider-settings',
      title: 'Call Provider Settings',
      subtitle: 'Manage providers, DIDs & routing',
      icon: Phone,
      color: '#0891b2',
      backgroundColor: '#f0f9ff',
      category: 'Integration & Tools'
    }
  ];

  const categories = [...new Set(moreOptions.map(option => option.category))];

  const handleOptionPress = (option) => {
    setActiveOption(option.id);
    
    switch (option.id) {
      case 'settings':
        navigate('/settings');
        break;
      case 'user-management':
        navigate('/users');
        break;
      case 'employee-attendance':
        navigate('/employee-attendance');
        break;
      case 'popular-services':
        navigate('/popular-services');
        break;
      case 'customer-banners':
        navigate('/customer-app-banners');
        break;
      case 'mechanic-banners':
        navigate('/mechanic-app-banners');
        break;
      case 'partner-training':
        navigate('/partner-training');
        break;
      case 'blog-admin':
        navigate('/blog-admin');
        break;
      case 'team-leaders':
        navigate('/team-leaders');
        break;
      case 'shipment-tracking':
        navigate('/shipment-tracking');
        break;
      case 'leads-csv-upload':
        navigate('/leads-csv-upload');
        break;
      case 'wfh-leads-csv-upload':
        navigate('/wfh-leads-csv-upload');
        break;
      case 'wfh-employee-report':
        navigate('/wfh-employee-report');
        break;
      case 'mechanic-csv-upload':
        navigate('/mechanic-csv-upload');
        break;
      case 'mechanic-management':
        navigate('/mechanic-management');
        break;
      case 'mechanic-kyc':
        navigate('/mechanic-kyc');
        break;
      case 'app-version-control':
        navigate('/app-version-control');
        break;
      case 'delivered-leads':
        navigate('/delivered-leads');
        break;
      case 'call-provider-settings':
        navigate('/call-provider-settings');
        break;
      case 'garage-management':
      case 'support-tickets':
      case 'system-maintenance':
      case 'backup-restore':
      case 'system-logs':
      case 'workflow-automation':
      case 'api-management':
      case 'data-analytics':
      case 'security-settings':
      case 'compliance-reports':
      case 'privacy-settings':
      case 'third-party-integrations':
      case 'developer-tools':
      case 'webhook-management':
        alert(`${option.title}\n\n${option.subtitle}\n\nThis feature is coming soon!`);
        break;
      default:
        alert(`${option.title}\n\n${option.subtitle}\n\nThis feature is coming soon!`);
        break;
    }
  };

  const getCategoryOptions = (category) => {
    return moreOptions.filter(option => option.category === category);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">More Options</h1>
        <p className="text-gray-600 mt-1">Advanced administrative features</p>
      </div>

      {/* Appearance */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Appearance</h2>
        <div className="flex space-x-3">
          <button
            onClick={() => setThemeMode('light')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition ${
              themeMode === 'light'
                ? 'bg-primary-500 text-white border-primary-500'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Sun size={18} />
            <span>Light</span>
          </button>
          <button
            onClick={() => setThemeMode('dark')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition ${
              themeMode === 'dark'
                ? 'bg-primary-500 text-white border-primary-500'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Moon size={18} />
            <span>Dark</span>
          </button>
          <button
            onClick={() => setThemeMode('system')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition ${
              themeMode === 'system'
                ? 'bg-primary-500 text-white border-primary-500'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Monitor size={18} />
            <span>System</span>
          </button>
        </div>
      </div>

      {/* Options by Category */}
      {categories.map(category => (
        <div key={category} className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">{category}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getCategoryOptions(category).map(option => {
              const Icon = option.icon;
              return (
                <button
                  key={option.id}
                  onClick={() => handleOptionPress(option)}
                  className={`bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition text-left relative h-full ${
                    activeOption === option.id ? 'ring-2 ring-primary-500' : ''
                  }`}
                >
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-3"
                    style={{ backgroundColor: option.backgroundColor }}
                  >
                    <Icon size={24} color={option.color} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{option.title}</h3>
                  <p className="text-sm text-gray-600">{option.subtitle}</p>
                  {activeOption === option.id && (
                    <div className="absolute top-4 right-4">
                      <CheckCircle size={16} className="text-green-500" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default MoreOptions;
